/**
 * Register Page
 * Split-screen layout with tenant creation form
 * Per design guidelines: Auto-generate slug, password strength indicator
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../components/auth/AuthLayout';
import { PasswordStrengthIndicator } from '../components/auth/PasswordStrengthIndicator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail, validatePassword, generateSlug } from '../utils/validation';
import { Eye, EyeOff, Loader2, AlertCircle, Building2 } from 'lucide-react';

const Register = () => {
  const [tenantName, setTenantName] = useState('');
  const [tenantSlug, setTenantSlug] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, any>>({});
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  // Auto-generate slug from tenant name
  useEffect(() => {
    if (tenantName && !errors.tenantSlug) {
      setTenantSlug(generateSlug(tenantName));
    }
  }, [tenantName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Reset errors
    setErrors({});
    setFormError('');

    // Validation
    const newErrors: Record<string, string> = {};

    if (!tenantName || tenantName.length < 2) {
      newErrors.tenantName = 'Tenant name must be at least 2 characters';
    }

    if (!tenantSlug || tenantSlug.length < 2) {
      newErrors.tenantSlug = 'Tenant slug must be at least 2 characters';
    } else if (!/^[a-z0-9-]+$/.test(tenantSlug)) {
      newErrors.tenantSlug = 'Slug must contain only lowercase letters, numbers, and hyphens';
    }

    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(password)) {
      newErrors.password =
        'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!acceptTerms) {
      newErrors.terms = 'You must accept the terms and conditions';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Submit
    setIsLoading(true);
    const result = await register({
      tenant_name: tenantName,
      tenant_slug: tenantSlug,
      email,
      password,
    });
    setIsLoading(false);

    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setFormError(result.error ?? 'Registration failed');
    }
  };

  return (
    <AuthLayout showSidePanel={true} sidePanelVariant="register">
      <Card className="w-full border-slate-200 shadow-lg rounded-2xl" data-testid="register-card">
        <CardHeader className="space-y-1 pb-6">
          <CardTitle
            className="text-2xl sm:text-3xl font-semibold text-slate-800"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            Create your account
          </CardTitle>
          <CardDescription className="text-base text-slate-600">
            Start your 14-day free trial - no credit card required
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Form Error */}
          {formError && (
            <Alert variant="destructive" className="mb-6" data-testid="form-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Registration Failed</AlertTitle>
              <AlertDescription>{formError}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tenant Name Field */}
            <div className="space-y-2">
              <Label htmlFor="tenantName" className="text-sm font-medium text-slate-700">
                Company Name
              </Label>
              <div className="relative">
                <Input
                  id="tenantName"
                  type="text"
                  placeholder="Acme Corporation"
                  value={tenantName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTenantName(e.target.value)
                  }
                  className="h-11 pl-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="tenant-name-input"
                  aria-invalid={errors.tenantName ? 'true' : 'false'}
                  aria-describedby={errors.tenantName ? 'tenant-name-error' : undefined}
                />
                <Building2
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={20}
                />
              </div>
              {errors.tenantName && (
                <p
                  id="tenant-name-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="tenant-name-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.tenantName}
                </p>
              )}
            </div>

            {/* Tenant Slug Field */}
            <div className="space-y-2">
              <Label htmlFor="tenantSlug" className="text-sm font-medium text-slate-700">
                Company Slug
              </Label>
              <Input
                id="tenantSlug"
                type="text"
                placeholder="acme-corp"
                value={tenantSlug}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTenantSlug(e.target.value)}
                className="h-11 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                data-testid="tenant-slug-input"
                aria-invalid={errors.tenantSlug ? 'true' : 'false'}
                aria-describedby={errors.tenantSlug ? 'tenant-slug-error' : 'tenant-slug-help'}
              />
              {!errors.tenantSlug && (
                <p id="tenant-slug-help" className="text-sm text-slate-500">
                  Used in your unique URL (lowercase letters, numbers, hyphens only)
                </p>
              )}
              {errors.tenantSlug && (
                <p
                  id="tenant-slug-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="tenant-slug-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.tenantSlug}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Work Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                className="h-11 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                data-testid="email-input"
                aria-invalid={errors.email ? 'true' : 'false'}
                aria-describedby={errors.email ? 'email-error' : undefined}
              />
              {errors.email && (
                <p
                  id="email-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="email-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                  className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="password-input"
                  aria-invalid={errors.password ? 'true' : 'false'}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  data-testid="password-toggle-button"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
              {errors.password && (
                <p
                  id="password-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="password-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter your password"
                  value={confirmPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setConfirmPassword(e.target.value)
                  }
                  className="h-11 pr-10 border-slate-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg transition-all duration-200"
                  data-testid="confirm-password-input"
                  aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                  aria-describedby={errors.confirmPassword ? 'confirm-password-error' : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors duration-200"
                  data-testid="confirm-password-toggle-button"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p
                  id="confirm-password-error"
                  className="text-sm text-red-600 flex items-center gap-1 mt-1"
                  role="alert"
                  data-testid="confirm-password-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="border-slate-300 data-[state=checked]:bg-primary-600 data-[state=checked]:border-primary-600 mt-1"
                  data-testid="terms-checkbox"
                  aria-invalid={errors.terms ? 'true' : 'false'}
                  aria-describedby={errors.terms ? 'terms-error' : undefined}
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-slate-700 cursor-pointer leading-relaxed"
                >
                  I agree to the{' '}
                  <Link
                    to="/app/terms"
                    className="text-primary-600 hover:underline"
                    target="_blank"
                  >
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/app/privacy"
                    className="text-primary-600 hover:underline"
                    target="_blank"
                  >
                    Privacy Policy
                  </Link>
                </label>
              </div>
              {errors.terms && (
                <p
                  id="terms-error"
                  className="text-sm text-red-600 flex items-center gap-1"
                  role="alert"
                  data-testid="terms-error"
                >
                  <AlertCircle size={16} aria-hidden="true" />
                  {errors.terms}
                </p>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg shadow-sm hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              data-testid="submit-button"
              aria-busy={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  <span>Creating account...</span>
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          {/* Sign In Link */}
          <div className="mt-6">
            <p className="text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link
                to="/app/login"
                className="font-medium text-primary-600 hover:text-primary-700 hover:underline"
                data-testid="login-link"
              >
                Sign in
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </AuthLayout>
  );
};

export default Register;
